/**
 * La demande de révision du code APE, et son tableau d'activité.
 *
 * L'INSEE traite ces demandes par un formulaire en ligne, auquel il faut
 * joindre « le tableau d'activité de l'unité dûment rempli ». Ce tableau est
 * publié en PDF, mais SANS champs : c'est un modèle à reproduire, pas un
 * imprimé à remplir. Ce module le reproduit donc à l'identique, colonnes,
 * intitulés et mentions comprises, et le remplit avec les activités déclarées.
 *
 * DEUX DEMANDES QUE TOUT LE MONDE CONFOND, et une seule passe par l'INSEE :
 *   - le code n'a jamais correspondu, c'est une ERREUR DE CODIFICATION : la
 *     demande va à l'INSEE, avec ce tableau ;
 *   - l'activité principale a changé depuis : la déclaration passe par le
 *     guichet unique, et l'INSEE en tire le nouveau code tout seul. Envoyer un
 *     tableau d'activité dans ce cas ne fait rien avancer.
 * La notice de l'INSEE ajoute une troisième porte, le changement « significatif
 * et durable » d'activité, qu'elle distingue des variations conjoncturelles ou
 * saisonnières, et elle prévient que des demandes successives non justifiées
 * dans la même année ne sont pas recevables.
 *
 * LES RÈGLES DE REMPLISSAGE VIENNENT DE LA NOTICE, page 2 du tableau, et elles
 * sont vérifiées ici parce qu'un tableau mal rempli revient sans être étudié :
 * une seule activité par ligne, sans accolade ni regroupement (« fabrication et
 * installation de volets » fait deux lignes), pas de code d'activité ni de nom
 * de profession dans la désignation, et un total de pourcentages qui fait
 * exactement cent.
 */

import { dateEnToutesLettres, identite, nettoyer } from "./documentText.js";

/** Ce que l'INSEE annonce comme délai de réponse, en jours ouvrés. */
export const DELAI_REPONSE_JOURS_OUVRES = 20;

/** Le SIREN, neuf chiffres, dont l'INSEE se sert pour retrouver l'unité. */
export function siren(brut) {
    const n = nettoyer(brut).replace(/\s/g, "");
    if (!n) return { valeur: "", motif: "vide" };
    if (!/^\d{9}$/.test(n)) return { valeur: n, motif: "format" };
    return { valeur: n, motif: null };
}

/**
 * Les noms d'action qui désignent une activité, et non son objet.
 *
 * Ils servent à distinguer les deux emplois du « et ». « Fabrication ET
 * installation de volets » relie deux actions, donc deux activités que la
 * notice veut sur deux lignes. « Création de logotypes ET d'identités
 * visuelles » relie deux objets d'une même création, et tient sur une seule :
 * la notice demande d'ailleurs, pour le commerce, de préciser « le ou les
 * produits concernés », donc plusieurs objets sur une ligne sont attendus.
 */
const NOMS_D_ACTION = [
    "achat", "conception", "conseil", "création", "dépannage", "développement",
    "distribution", "entretien", "fabrication", "formation", "hébergement",
    "installation", "location", "maintenance", "nettoyage", "pose", "production",
    "réalisation", "récupération", "rédaction", "réparation", "traduction",
    "transport", "vente",
];

/**
 * Ce que la notice reproche à une désignation.
 *
 * Deux défauts, de fiabilité inégale. Le code NAF est certain : la notice
 * interdit d'indiquer « de codes d'activité, ni de profession », et sa forme ne
 * trompe pas. Le regroupement de deux activités est probable seulement, d'où
 * une simple alerte côté validation.
 */
export function defautsDeDesignation(texte) {
    const t = nettoyer(texte);
    const defauts = [];
    if (!t) return defauts;

    // Un code NAF : quatre chiffres et une lettre, avec ou sans point.
    if (/\b\d{2}\.?\d{2}\s?[A-Za-z]\b/.test(t)) defauts.push("codeNaf");

    // Un connecteur encadré par deux noms d'action relie deux activités.
    const connecteur = /\b(et|ainsi que|puis)\b/i.exec(t);
    if (connecteur) {
        const action = (morceau) =>
            NOMS_D_ACTION.some((mot) =>
                new RegExp(`\\b${mot}s?\\b`, "i").test(
                    morceau.normalize("NFC"),
                ),
            );
        const avant = t.slice(0, connecteur.index);
        const apres = t.slice(connecteur.index + connecteur[0].length);
        if (action(avant) && action(apres)) defauts.push("regroupement");
    }

    return defauts;
}

/** La somme des pourcentages déclarés, arrondie au dixième. */
export function totalPourcentages(activites) {
    const somme = (activites || []).reduce((total, a) => {
        const part = Number(String(a?.part ?? "").replace(",", "."));
        return total + (Number.isFinite(part) ? part : 0);
    }, 0);
    return Math.round(somme * 10) / 10;
}

/** Les activités réellement renseignées, dans l'ordre de saisie. */
export function activitesRemplies(activites) {
    return (activites || []).filter((a) => nettoyer(a?.designation) || nettoyer(String(a?.part ?? "")));
}

/**
 * Qui doit s'adresser à l'INSEE, et qui doit passer ailleurs.
 *
 * Le tri le plus utile de l'outil : trois motifs, un seul mène ici.
 */
export function concerne(reponses) {
    const r = reponses || {};
    if (!r.motif) return { concerne: null, motif: null };
    if (r.motif === "changementActivite") return { concerne: false, motif: "changementActivite" };
    if (r.motif === "simpleGout") return { concerne: false, motif: "simpleGout" };
    return { concerne: true, motif: null };
}

/** Ce qui manque, ce qui est refusé, et ce qui mérite un mot. */
export function valider(reponses) {
    const r = reponses || {};
    const manques = [];
    const invalides = [];
    const alertes = [];

    if (!concerne(r).concerne) {
        return { valide: false, manques: ["motif"], invalides, alertes };
    }

    const numero = siren(r.siren);
    if (numero.motif === "vide") manques.push("siren");
    else if (numero.motif) invalides.push({ champ: "siren", motif: numero.motif });

    const requis = [
        ["prenoms", r.prenoms],
        ["nom", r.nom],
        ["fonction", r.fonction],
        ["mail", r.mail],
        ["lieuDeSignature", r.lieuDeSignature],
    ];
    for (const [cle, valeur] of requis) {
        if (!nettoyer(valeur)) manques.push(cle);
    }

    if (nettoyer(r.mail) && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nettoyer(r.mail))) {
        invalides.push({ champ: "mail", motif: "format" });
    }

    const brut = nettoyer(r.dateDeSignature);
    if (!brut) manques.push("dateDeSignature");
    else if (!dateEnToutesLettres(brut)) invalides.push({ champ: "dateDeSignature", motif: "format" });

    /* Le tableau lui-même : au moins une activité, des désignations conformes
       à la notice, et un total qui fait cent. Sans cela l'INSEE renvoie le
       document sans l'étudier. */
    const activites = activitesRemplies(r.activites);
    if (activites.length === 0) manques.push("activites");

    activites.forEach((activite, rang) => {
        if (!nettoyer(activite.designation)) {
            invalides.push({ champ: "activites", motif: "designationVide", rang });
        } else {
            for (const defaut of defautsDeDesignation(activite.designation)) {
                // Le code NAF est certain, le regroupement seulement probable :
                // bloquer sur une hypothèse ferait recommencer une désignation
                // pourtant juste.
                if (defaut === "codeNaf") invalides.push({ champ: "activites", motif: defaut, rang });
                else alertes.push(`${defaut}:${rang}`);
            }
        }
        const part = String(activite.part ?? "").replace(",", ".");
        if (!part) invalides.push({ champ: "activites", motif: "partVide", rang });
        else if (!/^\d{1,3}(\.\d)?$/.test(part) || Number(part) <= 0 || Number(part) > 100) {
            invalides.push({ champ: "activites", motif: "partHorsBornes", rang });
        }
    });

    if (activites.length > 0) {
        const total = totalPourcentages(activites);
        if (total !== 100) invalides.push({ champ: "activites", motif: "total", total });
    }

    // Une seule activité déclarée à 100 % est recevable, mais rappelle qu'un
    // code APE ne rend pas compte des activités secondaires.
    if (activites.length === 1) alertes.push("uneSeuleActivite");

    return {
        valide: manques.length === 0 && invalides.length === 0,
        manques: [...new Set(manques)],
        invalides,
        alertes,
    };
}

/** Le pourcentage écrit comme le tableau l'attend. */
export function partLisible(part) {
    const n = Number(String(part).replace(",", "."));
    if (!Number.isFinite(n)) return "";
    return `${String(n).replace(".", ",")} %`;
}

/**
 * Le tableau d'activité, en blocs, prêt à être posé sur une page ou dans un PDF.
 *
 * La structure suit celle de l'INSEE : le titre, le SIREN, les trois colonnes
 * de la liste des activités, le total, puis le répondant et la signature. Les
 * mentions du modèle sont reprises parce que ce sont elles que l'agent du site
 * Sirene s'attend à lire.
 */
export function composerTableau(reponses, aujourdhui) {
    const r = reponses || {};
    const dateDeSignature = nettoyer(r.dateDeSignature) || String(aujourdhui).slice(0, 10);
    const complet = { ...r, dateDeSignature };
    if (!valider(complet).valide) return null;

    const activites = activitesRemplies(r.activites);
    const nomComplet = identite({ prenoms: r.prenoms, nom: r.nom });

    return {
        titre: "Tableau d'activité pour contestation du code APE de votre entreprise",
        blocs: [
            {
                type: "paragraphe",
                texte:
                    "Le code APE de votre entreprise est déterminé à partir de la Nomenclature " +
                    "d'Activités Française (NAF). Ce tableau précise les activités réellement " +
                    "exercées, à joindre au formulaire de demande de révision accessible sur " +
                    "insee.fr.",
            },
            { type: "lignes", lignes: [`Numéro SIREN : ${siren(r.siren).valeur}`] },
            {
                type: "tableau",
                entetes: ["Désignation de l'activité", "Effectif salarié", "Pourcentage du C. A."],
                // Mesurées sur les en-têtes : « Pourcentage du C. A. » demande 38 mm
                // pour tenir sur une ligne, et le reste va à la désignation.
                largeurs: [90, 30, 40],
                /* Un effectif et un pourcentage sont des chiffres : ils
                   s'alignent à droite, sinon « 70 % » et « 100 % » ne tombent
                   pas sous la même unité et le total ne se lit plus sous sa
                   colonne. La notice de l'INSEE fixe les colonnes et leur
                   contenu, pas leur fer. */
                alignements: ["gauche", "droite", "droite"],
                lignes: activites.map((a) => [
                    nettoyer(a.designation),
                    String(nettoyer(String(a.effectif ?? "")) || "0"),
                    partLisible(a.part),
                ]),
                total: ["Total", "", "100 %"],
            },
            {
                type: "lignes",
                lignes: [
                    `Nom et fonction de la personne ayant répondu : ${nomComplet}, ${nettoyer(r.fonction)}`,
                    ...(nettoyer(r.telephone) ? [`Numéro de téléphone : ${nettoyer(r.telephone)}`] : []),
                    `Adresse courriel : ${nettoyer(r.mail)}`,
                ],
            },
            {
                type: "paragraphe",
                texte: `À ${nettoyer(r.lieuDeSignature)}, le ${dateEnToutesLettres(dateDeSignature)}.`,
            },
            { type: "signature", nom: nomComplet },
            {
                type: "note",
                texte:
                    "Une seule activité par ligne, sans code d'activité ni nom de profession, " +
                    "conformément à la notice de l'INSEE. Le tableau doit être revêtu du cachet " +
                    "de l'entreprise lorsqu'elle en possède un.",
            },
        ],
    };
}

/** Ce que le parcours annonce avant le téléchargement. */
export function recapitulatif(reponses, aujourdhui) {
    const r = reponses || {};
    if (!composerTableau(r, aujourdhui)) return null;
    const activites = activitesRemplies(r.activites);

    return [
        ["Numéro SIREN", siren(r.siren).valeur],
        ["Activités déclarées", `${activites.length}, dont la principale : ${nettoyer(activites[0].designation)}`],
        ["Répartition", activites.map((a) => partLisible(a.part)).join(" / ")],
        ["Répondant", `${identite({ prenoms: r.prenoms, nom: r.nom })}, ${nettoyer(r.fonction)}`],
        ["Réponse de l'INSEE", `sous ${DELAI_REPONSE_JOURS_OUVRES} jours ouvrés`],
    ];
}
